/**
 * scripts-components-product-card.js
 * -----------------------------------------------------------------------
 * Wishlist persistence, storefront-wide. No customer account/metafield
 * integration (that would require a Shopify app/backend), so state is
 * kept client-side in `localStorage`, keyed by product handle — the same
 * pattern already used for "recently viewed" in
 * scripts-components-recently-viewed.js. Every `[data-wishlist-toggle]`
 * button anywhere in the theme (product card, product page) shares this
 * one store, and every `[data-wishlist-count-badge]` reflects its size.
 *
 * Dispatches a `am:wishlist:change` event on `document` after every
 * mutation so scripts-components-wishlist.js (wishlist page only) can
 * re-render without a page reload.
 * ----------------------------------------------------------------------- */
(function () {
  'use strict';

  var STORAGE_KEY = 'am:wishlist';

  function readWishlist() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var handles = raw ? JSON.parse(raw) : [];
      return Array.isArray(handles) ? handles : [];
    } catch (error) {
      return [];
    }
  }

  function writeWishlist(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (error) {
      // Ignore storage errors (private browsing, quota, etc.).
    }
  }

  function isWishlisted(handle) {
    return readWishlist().indexOf(handle) !== -1;
  }

  function toggleWishlist(handle) {
    var handles = readWishlist();
    var index = handles.indexOf(handle);
    if (index === -1) {
      handles.push(handle);
    } else {
      handles.splice(index, 1);
    }
    writeWishlist(handles);
    return index === -1;
  }

  function syncButtons() {
    var handles = readWishlist();
    document.querySelectorAll('[data-wishlist-toggle][data-wishlist-handle]').forEach(function (button) {
      var handle = button.getAttribute('data-wishlist-handle');
      var saved = handles.indexOf(handle) !== -1;
      button.setAttribute('aria-pressed', String(saved));
      var label = button.getAttribute(saved ? 'data-wishlist-remove-label' : 'data-wishlist-add-label');
      if (label) button.setAttribute('aria-label', label);
    });
  }

  function syncBadges() {
    var count = readWishlist().length;
    document.querySelectorAll('[data-wishlist-count-badge]').forEach(function (badge) {
      badge.textContent = String(count);
      badge.hidden = count <= 0;
    });
  }

  function handleClick(event) {
    var button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    var handle = button.getAttribute('data-wishlist-handle');
    if (!handle) {
      // No product identifier attached: fall back to the old
      // visual-only toggle so the button never throws.
      var pressed = button.getAttribute('aria-pressed') === 'true';
      button.setAttribute('aria-pressed', String(!pressed));
      return;
    }
    var added = toggleWishlist(handle);
    syncButtons();
    syncBadges();
    document.dispatchEvent(new CustomEvent('am:wishlist:change', { detail: { handle: handle, added: added } }));
  }

  document.addEventListener('click', handleClick);
  document.addEventListener('shopify:section:load', function () { syncButtons(); syncBadges(); });
  window.addEventListener('storage', function (event) {
    if (event.key === STORAGE_KEY || event.key === null) {
      syncButtons();
      syncBadges();
      document.dispatchEvent(new CustomEvent('am:wishlist:change'));
    }
  });

  window.AniMarket = window.AniMarket || {};
  window.AniMarket.wishlist = {
    read: readWishlist,
    has: isWishlisted,
    // Re-reads localStorage and re-applies aria-pressed/badge state to
    // every matching element currently in the DOM. Call this after any
    // AJAX-appended markup introduces new [data-wishlist-toggle] buttons
    // (e.g. facets infinite-scroll pagination) whose server-rendered
    // aria-pressed="false" doesn't know about items already wishlisted
    // in a previous page/session.
    sync: function () {
      syncButtons();
      syncBadges();
    },
  };

  syncButtons();
  syncBadges();
})();
